/**
 * AdminEditorialMatches — curator queue for the `needs_review` rows
 * emitted by `enrich_editorial_from_hesa.py`.
 *
 * Each row shows the editorial degree the enricher wanted to enrich,
 * its top HESA candidate, and buttons to Merge, Skip, or Search for a
 * different HESA row.
 *
 * Endpoints:
 *   GET  /api/admin/editorial-matches?status=needs_review
 *   POST /api/admin/editorial-matches/candidates    (search)
 *   POST /api/admin/editorial-matches/merge         (curator merge)
 *   POST /api/admin/editorial-matches/dismiss       (skip permanently)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, Check, X, Search, ExternalLink, ShieldAlert, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Sub-table row. Kept small so the queue can render 50-100 items fast.
function MatchRow({ row, onAction, actionBusy }) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState(row.hesa_title || "");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await api.post("/admin/editorial-matches/candidates", { query });
      setHits(r.data.hits || []);
    } catch (e) {
      toast.error("Search failed");
    }
    setSearching(false);
  };

  return (
    <tr className="border-t border-stone-200" data-testid={`match-row-${row.editorial_id}`}>
      <td className="py-3 pr-3 align-top">
        <div className="text-sm font-medium">{row.editorial_title}</div>
        <div className="text-xs text-stone-500">{row.editorial_uni}</div>
        <div className="text-[10px] font-mono text-stone-400 mt-0.5">{row.editorial_id}</div>
        <Link
          to={`/degree/${row.editorial_id}`}
          target="_blank"
          className="text-[11px] text-[#4C1D95] hover:underline inline-flex items-center gap-1 mt-1"
          data-testid={`open-editorial-${row.editorial_id}`}
        >
          Preview <ExternalLink className="h-3 w-3" />
        </Link>
      </td>
      <td className="py-3 px-3 align-top">
        <div className="text-sm">{row.hesa_title || <em className="text-stone-400">— none —</em>}</div>
        <div className="text-[10px] font-mono text-stone-400 mt-0.5">{row.hesa_id || "no candidate"}</div>
        <div className="flex gap-3 mt-1 text-[11px] font-mono text-stone-500">
          <span>score {row.match_score}</span>
          <span>margin {row.margin_over_2}</span>
        </div>
      </td>
      <td className="py-3 pl-3 align-top">
        <div className="flex flex-wrap gap-2">
          {row.hesa_id && (
            <Button
              size="sm"
              disabled={actionBusy}
              onClick={() => onAction("merge", row.editorial_id, row.hesa_id)}
              data-testid={`btn-merge-${row.editorial_id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="h-3 w-3 mr-1" /> Merge with top match
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={actionBusy}
            onClick={() => setExpanded((e) => !e)}
            data-testid={`btn-browse-${row.editorial_id}`}
          >
            <Search className="h-3 w-3 mr-1" /> Browse alternatives
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={actionBusy}
            onClick={() => onAction("dismiss", row.editorial_id, null)}
            data-testid={`btn-dismiss-${row.editorial_id}`}
            className="text-stone-500 hover:text-stone-700"
          >
            <X className="h-3 w-3 mr-1" /> Skip
          </Button>
        </div>

        {expanded && (
          <div className="mt-3 p-3 rounded-md border border-stone-200 bg-stone-50">
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Search HESA course titles…"
                className="text-sm"
                data-testid={`search-input-${row.editorial_id}`}
              />
              <Button size="sm" onClick={runSearch} disabled={searching}
                      data-testid={`search-btn-${row.editorial_id}`}>
                {searching ? <Loader2 className="h-3 w-3 animate-spin" /> : "Search"}
              </Button>
            </div>
            {hits.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid={`search-hits-${row.editorial_id}`}>
                {hits.map((h) => (
                  <li
                    key={h.id}
                    className="flex items-center justify-between gap-2 text-[12px]"
                  >
                    <div>
                      <div className="text-stone-900">{h.official_title}</div>
                      <div className="text-[10px] text-stone-500">
                        {h.institution || "—"}
                        {h.typical_a_level && <> · {h.typical_a_level}</>}
                        {h.leo5_median_gbp != null && <> · £{h.leo5_median_gbp.toLocaleString()} LEO5</>}
                        {" · "}<span className="font-mono">{h.id}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => onAction("merge", row.editorial_id, h.id)}
                      disabled={actionBusy}
                      data-testid={`merge-alt-${row.editorial_id}-${h.id}`}
                      className="shrink-0"
                    >
                      Merge with this
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminEditorialMatches() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [status, setStatus] = useState("needs_review");
  const [loading, setLoading] = useState(true);
  const [actionBusyId, setActionBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/editorial-matches", { params: { status } });
      setRows(r.data.rows || []);
      setTotals(r.data.totals || {});
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        // let the un-admin state below render
      } else {
        toast.error(detail || "Could not load match queue");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading && user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, status]);

  const onAction = async (action, editorial_id, hesa_id) => {
    setActionBusyId(editorial_id);
    try {
      if (action === "merge") {
        await api.post("/admin/editorial-matches/merge", { editorial_id, hesa_id });
        toast.success("Merged with HESA row");
      } else if (action === "dismiss") {
        await api.post("/admin/editorial-matches/dismiss", { editorial_id });
        toast.success("Row dismissed from queue");
      }
      setRows((prev) => prev.filter((r) => r.editorial_id !== editorial_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setActionBusyId(null);
  };

  const remainingCounts = useMemo(() => (
    ["needs_review", "no_prn", "merged"].map((k) => ({ k, n: totals[k] || 0 }))
  ), [totals]);

  if (authLoading) {
    return <div className="p-8"><Loader2 className="animate-spin h-5 w-5" /></div>;
  }
  if (!user) {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <ShieldAlert className="h-8 w-8 mx-auto text-stone-500 mb-3" />
        <h1 className="font-editorial text-2xl">Sign in required</h1>
        <p className="text-sm text-stone-600 mt-2">
          The editorial-match queue is admin-only.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-editorial-matches">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
        data-testid="back-to-admin"
      >
        <ArrowLeft className="h-3 w-3" /> Back to Admin
      </Link>

      <h1 className="font-editorial text-3xl sm:text-4xl mt-3">
        Editorial ↔ HESA match queue
      </h1>
      <p className="text-sm text-stone-600 mt-2 max-w-2xl">
        The enricher couldn't confidently pick a HESA sibling for these
        editorial degrees. Pick the right one so the DegreeDetail page
        renders the full HESA-verified data stack.
      </p>

      <div className="mt-5 flex gap-2 text-xs">
        {remainingCounts.map(({ k, n }) => (
          <button
            key={k}
            onClick={() => setStatus(k)}
            data-testid={`filter-${k}`}
            className={`px-3 py-1.5 rounded-full border ${
              status === k
                ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
            }`}
          >
            {k.replace("_", " ")} · <span className="font-mono">{n}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-8 p-6 rounded-md border border-emerald-200 bg-emerald-50 text-sm text-emerald-900" data-testid="queue-empty">
          Queue is empty — nothing left to review under status <b>{status}</b>.
        </div>
      ) : (
        <table className="mt-6 w-full text-sm" data-testid="matches-table">
          <thead className="text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-300">
            <tr>
              <th className="text-left py-2 pr-3 w-1/3">Editorial degree</th>
              <th className="text-left py-2 px-3 w-1/3">Top HESA candidate</th>
              <th className="text-left py-2 pl-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <MatchRow
                key={r.editorial_id}
                row={r}
                onAction={onAction}
                actionBusy={actionBusyId === r.editorial_id}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
